import { memo } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { StateCard } from '@/lib/components/StateCard/StateCard';

import { useStateUserContext } from '@/src/application/context/UserContext';
import { useFeatureFlag } from '@/src/application/hooks/util/useFeatureFlag';
import ScrollToTop from '@/src/application/util/ScrollToTop';
import { V1CompatHandler } from '@/src/compat/V1CompatHandlerContainer';
import { V1TransitionSwitch } from '@/src/compat/V1TransitionSwitch';
import { useLastReports } from '@/src/infrastructure/services/useLastReports';
import AppLayout from '@/src/layouts/AppLayout';
import { DashboardContainer } from '@/src/presentation/screens/DashBoard';
import MainContainer from '@/src/presentation/screens/MainContainer';
import { IFreeTrialDemo } from '@/src/presentation/types/interfaces/IFreeTrialDemo';

import { CampaignsModuleRouter } from '@/modules/CampaignsModule/router/CampaignsModuleRouter';
import CreateSegmentModalProvider from '@/modules/ContactsModule/contexts/CreateSegmentModalContext';
import { ContactsModuleRouter } from '@/modules/ContactsModule/router/ContactsModuleRouter';
import { NpsModuleRouter } from '@/modules/NPSModule/router/NpsModuleRouter';

interface AppRouterVersionTwoProps {
  freeTrialBlockState: IFreeTrialDemo;
  contactsEnabled: boolean;
}

const AppRouterVersionTwo = ({
  freeTrialBlockState,
  contactsEnabled,
}: AppRouterVersionTwoProps) => {
  const [lastReports] = useLastReports({});
  const showNpsModule = useFeatureFlag('showNpsModule');
  const campaignsEnabled = useFeatureFlag('showCampaigns');

  const { userData } = useStateUserContext();

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route element={<AppLayout userData={userData} />} path="/v2">
          <Route
            index
            element={
              <>
                <V1CompatHandler enabled={false} targetIframe={'/?v2=true'} />
                {lastReports && lastReports?.length < 1 ? (
                  <DashboardContainer counter={freeTrialBlockState} userData={userData} />
                ) : (
                  <MainContainer
                    counter={freeTrialBlockState}
                    reports={lastReports}
                    userData={userData}
                  />
                )}
              </>
            }
          />
          <Route
            element={
              <>
                <V1TransitionSwitch enabled={false} />
                <V1CompatHandler enabled={true} targetIframe={'/?v2=true'} />
              </>
            }
            path="dashboard"
          />
          <Route
            element={
              <CreateSegmentModalProvider>
                {contactsEnabled ? (
                  <>
                    <ContactsModuleRouter />
                    <V1CompatHandler enabled={false} targetIframe={'/?v2=true'} />
                  </>
                ) : (
                  <>
                    <V1TransitionSwitch enabled={false} />
                    <V1CompatHandler enabled targetIframe={'/?v2=true'} />
                  </>
                )}
              </CreateSegmentModalProvider>
            }
            path="contacts/*"
          />
          <Route
            element={
              campaignsEnabled ? (
                <>
                  <CampaignsModuleRouter />
                  <V1CompatHandler enabled={false} targetIframe={'/?v2=true'} />
                </>
              ) : (
                <>
                  <V1TransitionSwitch enabled={false} />
                  <V1CompatHandler enabled={true} targetIframe={'/?v2=true'} />
                </>
              )
            }
            path="campaigns/*"
          />
          <Route
            element={
              <>
                <V1CompatHandler enabled targetIframe={'/?v2=true'} />
              </>
            }
            path="reports"
          />
          <Route
            element={
              <>
                <V1TransitionSwitch enabled={false} />
                <V1CompatHandler enabled targetIframe={'/?v2=true'} />
              </>
            }
            path="automation"
          />
          <Route
            element={
              <>
                <V1TransitionSwitch enabled={false} />
                <V1CompatHandler enabled targetIframe={'/?v2=true'} />
              </>
            }
            path="onsite"
          />
          <Route
            element={
              <>
                <V1TransitionSwitch enabled={false} />
                <V1CompatHandler enabled targetIframe={'/?v2=true'} />
              </>
            }
            path="configuration"
          />
          <Route
            element={
              <>
                <V1CompatHandler enabled targetIframe={`/${window.location.search}&v2=true`} />
              </>
            }
            path="sharing"
          />
          {showNpsModule && (
            <Route
              element={
                <>
                  <NpsModuleRouter />
                  <V1CompatHandler enabled={false} targetIframe={'/?v2=true'} />
                </>
              }
              path="nps/*"
            />
          )}
        </Route>
      </Routes>
      <StateCard />
    </BrowserRouter>
  );
};

export default memo(AppRouterVersionTwo);
